// FUNCTIONALITY-101/102 / CROSSSELL-109/110 — Store V2 product detail + pairs-well.
// CUSTOMER-R4.15-E — restore PDP cross-sell / recommended / recently viewed (lazy).
import { useEffect, useState, type ReactNode } from "react";
import type { CatalogV2Product } from "./catalog-v2-types.js";
import { ProductThumb } from "./components.js";
import { IsolatedSearchBar } from "./IsolatedSearchBar.js";
import { tileLabel } from "./taxonomy-v2.js";
import type { CrossSellCandidate } from "./cross-sell-rules.js";
import { CrossSellBlock } from "./CrossSellBlock.js";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { t } from "./store-v2-ui-copy.js";
import { resolveProductAvailability } from "./product-availability-v2.js";
import { resolveCustomerVisiblePrice } from "./resolve-customer-visible-price-v2.js";
import { awaitPromoOverlayPrices, ensurePromoPriceOverlayLoaded } from "./promo-price-overlay-v2.js";
import { formatUah } from "./money-v2.js";
import { resolveCustomerFacingCode, resolveIdentityRows } from "./product-identity-v2.js";
import {
  buildNutritionDisplayRows,
  hasDisplayableNutrition,
  nutritionBasisLabel,
  nutritionDisclaimer,
  nutritionSourceBadge,
} from "./nutrition-v2.js";
import { copyText } from "./contact-manager-v2.js";
import { loadPdpMerchandising } from "./pdp-merch-load-v2.js";
import { recordRecentProductShell } from "./recent-products-v2.js";
import { emitEvent } from "./events-v2.js";
import { resolveProductTitle } from "./product-title-resolver.js";
import type { ProductRecommendationItem } from "./product-recommendations-v2.js";

export interface ProductV2Props {
  lang: StoreV2Lang;
  product: CatalogV2Product;
  displayTitle: string;
  recommendations: CrossSellCandidate[];
  onAdd: (product: CatalogV2Product) => void;
  onGoCart: () => void;
  onAddRecommendation: (product: CatalogV2Product, surface?: string) => void;
  onOpenRecommendation: (product: CatalogV2Product) => void;
  /** R7.2.2F2 — register recommendation shells before open/add (Cart known-good pattern). */
  onRegisterActionable?: (products: CatalogV2Product[]) => void;
  /** R7.2.2F4C — current SKU line qty (stepper only). */
  inCartQty: number;
  /** R7.2.2F4C — total cart qty for primary "У кошику: N · Перейти" (must match nav badge). */
  cartTotalQty: number;
  qtyOf?: (productId: string) => number;
  onInc?: () => void;
  onDec?: () => void;
  onSearch: (query: string) => void;
  visualSearchSlot?: ReactNode;
  onAvailabilityRequest: () => void;
  availabilityMessage?: string | null;
  onContactManager?: () => void;
  /** COMMERCE-176C-R1 — shell opened from card; description/recs may still hydrate. */
  shellHydrating?: boolean;
}

export function ProductV2({
  lang,
  product,
  displayTitle,
  recommendations,
  onAdd,
  onGoCart,
  onAddRecommendation,
  onOpenRecommendation,
  onRegisterActionable,
  inCartQty,
  cartTotalQty,
  qtyOf,
  onInc,
  onDec,
  onSearch,
  visualSearchSlot,
  onAvailabilityRequest,
  availabilityMessage = null,
  onContactManager,
  shellHydrating = false,
}: ProductV2Props) {
  const [added, setAdded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [skuCopied, setSkuCopied] = useState(false);
  const [promoTick, setPromoTick] = useState(0);
  const [crossSell, setCrossSell] = useState<ProductRecommendationItem[]>([]);
  const [recommended, setRecommended] = useState<ProductRecommendationItem[]>([]);
  const [recentlyViewed, setRecentlyViewed] = useState<CrossSellCandidate[]>([]);
  const availability = resolveProductAvailability(product);
  const price = resolveCustomerVisiblePrice(product);
  void promoTick;

  useEffect(() => {
    ensurePromoPriceOverlayLoaded();
    let cancelled = false;
    void awaitPromoOverlayPrices(product.id).then(() => {
      if (!cancelled) setPromoTick((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [product.id]);

  // CUSTOMER-R4.15-E — record recent + lazy merch (never block first paint on full catalog).
  useEffect(() => {
    recordRecentProductShell(product);
    emitEvent("product_viewed", { product_id: product.id });
    let cancelled = false;
    // Seed from parent if present (full-bundle path), then refresh from shard pools.
    if (recommendations.length > 0) {
      setCrossSell(
        recommendations.slice(0, 3).map((c, idx) => ({
          ...c,
          placement: "PDP_CROSS_SELL" as const,
          strategy: "cross_sell_rules" as const,
          rank: idx + 1,
        })),
      );
    }
    void loadPdpMerchandising(product, lang).then((merch) => {
      if (cancelled) return;
      setCrossSell(merch.crossSell);
      setRecommended(merch.recommended);
      setRecentlyViewed(
        merch.recentlyViewed.map((p) => ({
          product: p,
          title: resolveProductTitle(p, lang).title,
          reasonCategory: p.categoryKeys[0] ?? "recent",
        })),
      );
      const shells = [
        ...merch.crossSell.map((c) => c.product),
        ...merch.recommended.map((c) => c.product),
        ...merch.recentlyViewed,
      ];
      onRegisterActionable?.(shells);
    });
    return () => {
      cancelled = true;
    };
  }, [product.id, lang, onRegisterActionable]); // recommendations seed applied once via parent; shard pool is SoT

  const emitRecImpression =
    (placement: string, strategy: string) => (ids: string[]) => {
      for (let i = 0; i < ids.length; i += 1) {
        emitEvent("recommendation_impression", {
          placement,
          source_product_id: product.id,
          recommended_product_id: ids[i],
          rank: i + 1,
          strategy,
          release: "R4.15-E",
        });
      }
    };

  const wrapAdd = (placement: string, strategy: string) => (rec: CatalogV2Product) => {
    emitEvent("recommendation_add_attempt", {
      placement,
      source_product_id: product.id,
      recommended_product_id: rec.id,
      strategy,
      release: "R7.2.2F2",
    });
    emitEvent("recommendation_add_to_cart", {
      placement,
      source_product_id: product.id,
      recommended_product_id: rec.id,
      strategy,
      release: "R4.15-E",
    });
    onAddRecommendation(rec, placement);
  };

  const wrapOpen = (placement: string, strategy: string) => (rec: CatalogV2Product) => {
    emitEvent("recommendation_click", {
      placement,
      source_product_id: product.id,
      recommended_product_id: rec.id,
      strategy,
      release: "R4.15-E",
    });
    onOpenRecommendation(rec);
  };
  const firstKey = product.categoryKeys[0];
  const catLabel = firstKey ? tileLabel(firstKey, lang) : null;
  const facing = resolveCustomerFacingCode({
    merchantSku: product.merchantSku,
    merchantArticle: product.merchantArticle,
    barcodeGtin: product.barcodeGtin,
    barcodeVerification: product.barcodeVerification,
  });
  const identityRows = resolveIdentityRows(lang, {
    merchantSku: product.merchantSku,
    merchantArticle: product.merchantArticle,
    manufacturerPartNumber: product.manufacturerPartNumber,
    originalSkuVerified: product.originalSkuVerified === true,
    barcodeGtin: product.barcodeGtin,
    barcodeVerification: product.barcodeVerification,
  });

  const meta: { label: string; value: string; copyable?: boolean }[] = [
    ...identityRows.map((row) => ({
      label: row.label,
      value: row.value,
      copyable: row.kind === "store_sku" || row.kind === "barcode",
    })),
    ...(product.brand ? [{ label: t(lang, "brand"), value: product.brand }] : []),
    ...(product.country ? [{ label: t(lang, "country"), value: product.country }] : []),
    ...(product.weight ? [{ label: t(lang, "weight"), value: product.weight }] : []),
  ];

  const handleCopySku = async (value: string) => {
    if (await copyText(value)) {
      setSkuCopied(true);
      window.setTimeout(() => setSkuCopied(false), 1500);
    }
  };

  const handleAdd = () => {
    if (!product.isSellable || busy) return;
    setBusy(true);
    onAdd(product);
    setAdded(true);
    window.setTimeout(() => {
      setBusy(false);
      setAdded(false);
    }, 1200);
  };

  return (
    <div className="sv2-body sv2-route-top" data-route-top="product">
      <div className="sv2-sticky-tools sv2-sticky-tools-compact sv2-pdp-search">
        <IsolatedSearchBar
          lang={lang}
          onSubmit={(q) => {
            const t = q.trim();
            if (t.length >= 2) onSearch(t);
          }}
          visualSlot={visualSearchSlot}
        />
      </div>
      <ProductThumb
        product={product}
        className="sv2-pdp-img"
        alt={displayTitle}
        previewable
        variant="pdp"
        mediaVariant="original"
        fetchPriority="high"
      />
      <h1 className="sv2-pdp-title">{displayTitle}</h1>
      {catLabel ? <div className="sv2-pdp-cat">{catLabel}</div> : null}
      {shellHydrating ? (
        <p className="sv2-muted" data-testid="sv2-pdp-shell-hydrating" style={{ fontSize: 12, marginTop: 4 }}>
          {lang === "en" ? "Loading details…" : "Завантажуємо деталі…"}
        </p>
      ) : null}

      {product.isPriced ? (
        <div className="sv2-pdp-price" style={{ marginTop: 12 }}>
          {price.saleActive ? (
            <>
              <span className="sv2-price-sale">{formatUah(price.primary)}</span>{" "}
              <span className="sv2-price-was">{formatUah(price.regular ?? 0)}</span>
              {price.badge ? <span className="sv2-price-badge">{price.badge}</span> : null}
            </>
          ) : (
            formatUah(price.primary)
          )}
        </div>
      ) : (
        <div className="sv2-pdp-price sv2-unpriced" style={{ marginTop: 12 }}>
          {t(lang, "price_tbd")}
        </div>
      )}

      <div className={`sv2-availability sv2-availability-${availability.state}`}>
        {availability.state === "available"
          ? t(lang, "available")
          : availability.state === "unavailable"
            ? t(lang, "not_available")
            : t(lang, "availability_unknown")}
      </div>

      {!product.isSellable ? (
        <div className="sv2-alert sv2-alert-err" style={{ marginTop: 12 }}>
          {product.sellabilityReason === "out_of_stock" ? t(lang, "oos") : t(lang, "unpriced")}
        </div>
      ) : null}

      <div className="sv2-meta">
        {meta.map((m) => (
          <div key={m.label} className="sv2-meta-row" data-testid={m.copyable ? "sv2-pdp-sku-row" : undefined}>
            <span className="sv2-meta-label">{m.label}</span>
            <span className="sv2-meta-value-group">
              <span className="sv2-meta-value">{m.value}</span>
              {m.copyable ? (
                <button
                  type="button"
                  className="sv2-copy-btn sv2-copy-inline"
                  data-testid="sv2-pdp-sku-copy"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    void handleCopySku(m.value);
                  }}
                >
                  {skuCopied ? t(lang, "copied") : t(lang, "copy")}
                </button>
              ) : null}
            </span>
          </div>
        ))}
      </div>

      {product.nutrition && hasDisplayableNutrition(product.nutrition) ? (
        <div className="sv2-nutrition" data-testid="sv2-pdp-nutrition">
          <h2 className="sv2-nutrition-title">
            {lang === "en" ? "Nutrition" : "Харчова цінність"}
          </h2>
          <p className="sv2-nutrition-basis">
            {nutritionBasisLabel(lang === "en" ? "en" : "ua", product.nutrition.nutrition_basis)}
            {product.nutrition.serving_size
              ? ` · ${lang === "en" ? "serving" : "порція"}: ${product.nutrition.serving_size}`
              : ""}
          </p>
          <div className="sv2-nutrition-badge" data-testid="sv2-nutrition-badge">
            {nutritionSourceBadge(lang === "en" ? "en" : "ua", product.nutrition.nutrition_quality)}
          </div>
          <table className="sv2-nutrition-table">
            <tbody>
              {buildNutritionDisplayRows(lang === "en" ? "en" : "ua", product.nutrition).map((row) => (
                <tr key={`${row.label}-${row.value}`}>
                  <th scope="row">{row.label}</th>
                  <td>{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="sv2-nutrition-disclaimer" data-testid="sv2-nutrition-disclaimer">
            {nutritionDisclaimer(lang === "en" ? "en" : "ua", product.nutrition.nutrition_quality)}
          </p>
          {product.nutrition.nutrition_source_name || product.nutrition.nutrition_source_url ? (
            <details className="sv2-nutrition-source" data-testid="sv2-nutrition-source">
              <summary>{lang === "en" ? "Source details" : "Джерело даних"}</summary>
              <ul>
                {product.nutrition.nutrition_quality ? (
                  <li>
                    {lang === "en" ? "Class" : "Клас"}: {product.nutrition.nutrition_quality}
                  </li>
                ) : null}
                {product.nutrition.nutrition_source_name ? (
                  <li>{product.nutrition.nutrition_source_name}</li>
                ) : null}
                {product.nutrition.nutrition_source_url ? (
                  <li>
                    <a href={product.nutrition.nutrition_source_url} target="_blank" rel="noreferrer">
                      {product.nutrition.nutrition_source_url}
                    </a>
                  </li>
                ) : null}
                {product.nutrition.nutrition_source_retrieved_at ? (
                  <li>
                    {lang === "en" ? "Retrieved" : "Отримано"}:{" "}
                    {product.nutrition.nutrition_source_retrieved_at}
                  </li>
                ) : null}
                <li>
                  {lang === "en" ? "Status" : "Статус"}:{" "}
                  {product.nutrition.nutrition_verified
                    ? lang === "en"
                      ? "verified"
                      : "перевірено"
                    : lang === "en"
                      ? "approximate / reference"
                      : "орієнтовно / довідково"}
                </li>
              </ul>
            </details>
          ) : null}
        </div>
      ) : null}

      {inCartQty > 0 && onInc && onDec ? (
        <div
          className="sv2-qty-stepper sv2-qty-stepper-lg"
          style={{ marginTop: 12 }}
          data-testid="sv2-pdp-sku-qty"
          data-sku-qty={inCartQty}
        >
          <button type="button" className="sv2-qty-btn" onClick={onDec}>
            −
          </button>
          <span className="sv2-qty-val">{inCartQty}</span>
          <button type="button" className="sv2-qty-btn" disabled={!product.isSellable} onClick={onInc}>
            +
          </button>
        </div>
      ) : availability.canAdd ? (
        <button
          type="button"
          className={`sv2-cta${added ? " sv2-add-pulse" : ""}`}
          disabled={busy}
          onClick={handleAdd}
        >
          {added ? t(lang, "added_btn") : t(lang, "add_to_cart")}
        </button>
      ) : (
        <button
          type="button"
          className="sv2-cta"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onAvailabilityRequest();
          }}
        >
          {t(lang, "availability_request")}
        </button>
      )}

      {availabilityMessage ? (
        <div className="sv2-alert sv2-alert-ok" style={{ marginTop: 12, whiteSpace: "pre-line" }}>
          {availabilityMessage}
        </div>
      ) : null}
      {availabilityMessage && onContactManager ? (
        <button
          type="button"
          className="sv2-cta sv2-secondary"
          style={{ marginTop: 8 }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onContactManager();
          }}
        >
          {t(lang, "contact_manager")}
        </button>
      ) : null}

      {inCartQty > 0 ? (
        <button
          type="button"
          className="sv2-cta sv2-cart-primary"
          data-testid="sv2-pdp-cart-cta"
          data-cart-total-qty={cartTotalQty}
          data-sku-qty={inCartQty}
          onClick={onGoCart}
        >
          {t(lang, "cart_cta_go", { qty: String(cartTotalQty) })}
        </button>
      ) : null}

      <CrossSellBlock
        lang={lang}
        items={crossSell}
        title={t(lang, "pairs_well")}
        testId="sv2-pdp-cross-sell"
        onAdd={wrapAdd("PDP_CROSS_SELL", "cross_sell_rules")}
        onOpen={wrapOpen("PDP_CROSS_SELL", "cross_sell_rules")}
        onOpenCart={onGoCart}
        qtyOf={(id) => qtyOf?.(id) ?? (id === product.id ? inCartQty : 0)}
        variant="pdp"
        excludeProductId={product.id}
        {...(onRegisterActionable ? { onRegisterActionable } : {})}
        onImpression={emitRecImpression("PDP_CROSS_SELL", "cross_sell_rules")}
      />
      <CrossSellBlock
        lang={lang}
        items={recommended}
        title={t(lang, "may_like")}
        testId="sv2-pdp-recommended"
        onAdd={wrapAdd("PDP_MAY_LIKE", "brand_country_complement")}
        onOpen={wrapOpen("PDP_MAY_LIKE", "brand_country_complement")}
        onOpenCart={onGoCart}
        qtyOf={(id) => qtyOf?.(id) ?? (id === product.id ? inCartQty : 0)}
        variant="pdp"
        excludeProductId={product.id}
        {...(onRegisterActionable ? { onRegisterActionable } : {})}
        onImpression={emitRecImpression("PDP_MAY_LIKE", "brand_country_complement")}
      />
      <CrossSellBlock
        lang={lang}
        items={recentlyViewed}
        title={t(lang, "recently_viewed")}
        testId="sv2-pdp-recently-viewed"
        onAdd={wrapAdd("PDP_RECENTLY_VIEWED", "recently_viewed")}
        onOpen={wrapOpen("PDP_RECENTLY_VIEWED", "recently_viewed")}
        onOpenCart={onGoCart}
        qtyOf={(id) => qtyOf?.(id) ?? (id === product.id ? inCartQty : 0)}
        variant="pdp"
        excludeProductId={product.id}
        {...(onRegisterActionable ? { onRegisterActionable } : {})}
        onImpression={emitRecImpression("PDP_RECENTLY_VIEWED", "recently_viewed")}
      />
    </div>
  );
}
